import Phaser from "phaser";

/**
 * Title-screen intro: the two founders (Lucas from the left, Dan from the right)
 * run in, meet in the middle, and high-five, then hold the high-five on a loop
 * until the player hits Start. Three source sheets, all on a shared 256x169 cell:
 *
 *   intro-run     - BOTH characters running (in place) in one frame. We show it
 *                   TWICE, each sprite cropped to one half, and slide the halves in
 *                   from the screen edges so they recombine into the full frame at
 *                   center. Same animation, just masked - that's the trick.
 *   intro-connect - the high-five: the lunge + contact + spark burst (one-shot).
 *   intro-loop    - the sustained high-five with pulsing sparks (loops forever).
 *
 * The two run halves come together at the center anchor, then we swap to the single
 * full-frame connect sprite at the SAME anchor so the hand-off is seamless; connect
 * then flows straight into loop (loop is the sustain of connect's final frame).
 */
export const INTRO = {
  run: { key: "intro-run", frames: 62, cols: 8 },
  connect: { key: "intro-connect", frames: 42, cols: 7 },
  loop: { key: "intro-loop", frames: 24, cols: 5 },
} as const;

export const INTRO_CELL = { w: 256, h: 169 };

// The vertical column that divides Lucas (left) from Dan (right) in the run frames.
// Found empirically: the gap between the two runners is empty at x=131 in all but a
// single frame (a 4px fingertip), so the crop never clips either character.
const SPLIT = 131;

const RUN_FPS = 16; // base leg-cycle rate; scaled at runtime by anims.timeScale (below)
// The run-in eases in (slow while small/far, fast while big/close), so the leg
// cadence should track it: multiply RUN_FPS from START to END across the run-in.
// ~8fps plod when distant, ~26fps sprint as they arrive, matching the travel.
const RUN_TS_START = 0.5;
const RUN_TS_END = 1.6;
const CONNECT_FPS = 24;
const LOOP_FPS = 16;

// The platform strip the runners stand on. Tiled endlessly and scaled with the
// runners so the ground zooms in with them (it can't be baked into the far bg, which
// stays distant). Transparent above ~y30; the deck surface the feet plant on is at
// FLOOR_DECK_FRAC of the strip's height.
const FLOOR_TEX = "intro-floor";
const FLOOR_H = 108;
const FLOOR_DECK_FRAC = 0.3;
const FLOOR_TILE_W = 3400; // must exceed 960 / startScale so the strip spans edge-to-edge even
// at the small distant start scale (~0.35): 2600 left slivers at each edge, so a tile is added
// to each side. At full scale it just extends further off-screen (harmless).

export function loadIntro(scene: Phaser.Scene) {
  for (const v of Object.values(INTRO)) {
    scene.load.spritesheet(v.key, `assets/sprites/${v.key}.png`, {
      frameWidth: INTRO_CELL.w,
      frameHeight: INTRO_CELL.h,
    });
  }
  scene.load.image(FLOOR_TEX, `assets/sprites/${FLOOR_TEX}.png`);
  scene.load.spritesheet(TITLE_LOGO_TEX, `assets/sprites/${TITLE_LOGO_TEX}.png`, {
    frameWidth: TITLE_LOGO_FW,
    frameHeight: TITLE_LOGO_FH,
  });
}

// Animated chrome CODE&CO wordmark (a slow light sweep). Uniform 6x11 spritesheet of
// 413x93 frames, 61 filled (last row has one). It yoyos so the sweep loops seamlessly.
const TITLE_LOGO_TEX = "title-logo";
const TITLE_LOGO_FW = 413;
const TITLE_LOGO_FH = 93;
const TITLE_LOGO_FRAMES = 61;
const TITLE_LOGO_ANIM = "title-logo-shine";

/** Place the animated wordmark at (x, y) at a fixed size (no zoom). Yoyos forever. */
export function playTitleLogo(
  scene: Phaser.Scene,
  opts: { x: number; y: number; scale?: number; fps?: number; depth?: number },
) {
  if (!scene.anims.exists(TITLE_LOGO_ANIM)) {
    scene.anims.create({
      key: TITLE_LOGO_ANIM,
      frames: scene.anims.generateFrameNumbers(TITLE_LOGO_TEX, { start: 0, end: TITLE_LOGO_FRAMES - 1 }),
      frameRate: opts.fps ?? 18,
      yoyo: true, // sweep runs out and back so the loop is seamless without a wrap frame
      repeat: -1,
    });
  }
  const logo = scene.add.sprite(opts.x, opts.y, TITLE_LOGO_TEX, 0).setScale(opts.scale ?? 1).setDepth(opts.depth ?? 6);
  logo.play(TITLE_LOGO_ANIM);
  return logo;
}

export function registerIntroAnims(scene: Phaser.Scene) {
  const mk = (key: string, frames: number, fps: number, repeat: number, yoyo = false) => {
    if (scene.anims.exists(key)) return;
    scene.anims.create({
      key,
      frames: scene.anims.generateFrameNumbers(key, { start: 0, end: frames - 1 }),
      frameRate: fps,
      repeat,
      yoyo,
    });
  };
  mk(INTRO.run.key, INTRO.run.frames, RUN_FPS, -1); // loops while sliding in
  mk(INTRO.connect.key, INTRO.connect.frames, CONNECT_FPS, 0); // one-shot high-five
  mk(INTRO.loop.key, INTRO.loop.frames, LOOP_FPS, -1); // sustained high-five, plain repeat
}

export interface IntroOptions {
  x: number; // center anchor (where the two halves recombine)
  y: number;
  scale?: number; // display scale of the 256x169 cell (default 2)
  depth?: number; // render depth (default 3)
  slideMs?: number; // how long the run-in takes (default 1100)
  slideFrom?: number; // extra px each half starts off past its resting spot (default 300)
  startScale?: number; // scale of the runners at the far end of the run-in (default scale/8)
  footFrac?: number; // vertical origin (0..1): the point that pins to `y` and that the zoom
  // scales around. Default 0.5 (frame center). Set to the feet fraction (~0.92 for the run
  // frames) with `y` on a floor line so the runners plant on it and grow in place instead of
  // sinking as they scale up.
  startDelay?: number; // ms of just-background-and-walkway before the runners enter (default 0)
  onHighFive?: () => void; // fired once the high-five lands (connect -> loop); e.g. reveal the title
}

/**
 * Build and play the full intro on `scene`. Returns a handle so the caller can
 * tear it down early (e.g. if the scene leaves before the sequence ends). The
 * loop phase repeats until the scene is stopped by the Start button.
 */
export function playIntro(scene: Phaser.Scene, opts: IntroOptions) {
  registerIntroAnims(scene);
  const { x, y } = opts;
  const scale = opts.scale ?? 2;
  const depth = opts.depth ?? 3;
  const slideMs = opts.slideMs ?? 1100;
  const slideFrom = opts.slideFrom ?? 300;
  const startScale = opts.startScale ?? scale / 4;
  const footFrac = opts.footFrac ?? 0.5;
  const startDelay = opts.startDelay ?? 0;

  const objs: Phaser.GameObjects.GameObject[] = [];
  let torn = false;
  const sprite = (key: string) => {
    // Origin y at the feet so `y` is a floor line and the zoom scales around the feet
    // (they stay planted) rather than the frame center (feet sink as it grows). x stays
    // centered so the two run halves still split/recombine around the frame centre.
    const s = scene.add.sprite(x, y, key, 0).setOrigin(0.5, footFrac).setScale(scale).setDepth(depth);
    objs.push(s);
    return s;
  };

  // --- walkway: shown immediately at the small start scale (distant), so the scene opens on
  // just the background and a far platform. It then zooms in TOGETHER with the runners (its
  // scale tween is started in startRun) so ground and characters grow as one. Tiled so it
  // never seams across the width.
  const floor = scene.add
    .tileSprite(x, y, FLOOR_TILE_W, FLOOR_H, FLOOR_TEX)
    .setOrigin(0.5, FLOOR_DECK_FRAC)
    .setDepth(depth - 1)
    .setScale(startScale);
  objs.push(floor);

  // --- runners: created after `startDelay` so the walkway sits alone first, then Lucas and
  // Dan run in from the sides, grow as they near, meet and high-five. onHighFive fires when
  // the high-five lands so the caller can reveal the title.
  const startRun = () => {
    if (torn) return;
    const lucas = sprite(INTRO.run.key);
    const dan = sprite(INTRO.run.key);
    // Crop each to its half (frame-local px, valid across frames since all are 256x169).
    lucas.setCrop(0, 0, SPLIT, INTRO_CELL.h);
    dan.setCrop(SPLIT, 0, INTRO_CELL.w - SPLIT, INTRO_CELL.h);
    lucas.play(INTRO.run.key);
    dan.play(INTRO.run.key);
    // Start off to the sides AND tiny; a cropped sprite still positions/scales by its
    // full-frame center, so both the x slide and the growth stay anchored at (x,y).
    lucas.x = x - slideFrom;
    dan.x = x + slideFrom;
    lucas.setScale(startScale);
    dan.setScale(startScale);
    lucas.anims.timeScale = RUN_TS_START;
    dan.anims.timeScale = RUN_TS_START;

    const toConnect = () => {
      if (torn) return;
      lucas.destroy();
      dan.destroy();
      const con = sprite(INTRO.connect.key);
      con.play(INTRO.connect.key);
      con.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        if (torn) return;
        con.destroy();
        const loop = sprite(INTRO.loop.key);
        loop.play(INTRO.loop.key); // holds the high-five until the scene changes
        opts.onHighFive?.(); // high-five landed: caller reveals the title now
      });
    };

    // Slide both halves to the center anchor; when they meet, hand off to connect.
    // Ease-in (matched to the growth) so they travel slowly while small and far, then rush
    // the last stretch as they get big and close, landing on the high-five at the same beat.
    scene.tweens.add({ targets: [lucas, dan], x, duration: slideMs, ease: "Quad.easeIn", onComplete: toConnect });
    // Grow the runners AND the walkway from small to full over the same run-in, same ease,
    // so ground and characters zoom in as one (perspective).
    scene.tweens.add({ targets: [lucas, dan, floor], scaleX: scale, scaleY: scale, duration: slideMs, ease: "Quad.easeIn" });
    // Quicken the leg cadence on the same curve so the feet track the accelerating slide.
    scene.tweens.add({ targets: [lucas.anims, dan.anims], timeScale: RUN_TS_END, duration: slideMs, ease: "Quad.easeIn" });
  };

  if (startDelay > 0) scene.time.delayedCall(startDelay, startRun);
  else startRun();

  return {
    destroy() {
      torn = true;
      objs.forEach((o) => o.destroy());
    },
  };
}

/** Jump straight to the sustained high-five loop at the final position, skipping
 *  the run-in and connect animations entirely. Returns the loop sprite. */
export function skipToIntroLoop(
  scene: Phaser.Scene,
  opts: { x: number; y: number; scale?: number; depth?: number; footFrac?: number },
) {
  registerIntroAnims(scene);
  const scale = opts.scale ?? 2;
  const depth = opts.depth ?? 3;
  const footFrac = opts.footFrac ?? 0.5;
  const floor = scene.add
    .tileSprite(opts.x, opts.y, FLOOR_TILE_W, FLOOR_H, FLOOR_TEX)
    .setOrigin(0.5, FLOOR_DECK_FRAC)
    .setDepth(depth - 1)
    .setScale(scale);
  const loop = scene.add
    .sprite(opts.x, opts.y, INTRO.loop.key, 0)
    .setOrigin(0.5, footFrac)
    .setScale(scale)
    .setDepth(depth);
  loop.play(INTRO.loop.key);
  return { floor, loop };
}
